import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import Logo from "@/components/Logo";
import ProductTicker from "@/components/ProductTicker";
import AnnouncementBar from "@/components/AnnouncementBar";
import {
  ShoppingCart, User, LogOut, LayoutDashboard, Menu, ChevronDown, ChevronRight,
  Facebook, Instagram, Youtube, Twitter, Linkedin, Send,
  // Product category icons
  Leaf, Sparkles, Droplet, GraduationCap, Gift, ShoppingBag, Shirt, Wheat,
  // Opportunity icons
  Target, UserPlus, TrendingUp, Trophy,
  // Offers icons
  Tag, Star,
  // Gallery icons
  Award, CalendarDays, Plane, Medal,
  // Legal icons
  FileText, ShieldCheck, RotateCcw, FileCheck2, AlertTriangle,
  // Utility
  Home as HomeIcon, Info, Phone,
} from "lucide-react";

const SOCIAL_LINKS = [
  { Icon: Facebook,  href: "https://facebook.com/samarawiz",  label: "Facebook" },
  { Icon: Instagram, href: "https://instagram.com/samarawiz", label: "Instagram" },
  { Icon: Youtube,   href: "https://youtube.com/@samarawiz",  label: "YouTube" },
  { Icon: Twitter,   href: "https://twitter.com/samarawiz",   label: "Twitter" },
  { Icon: Linkedin,  href: "https://linkedin.com/company/samarawiz", label: "LinkedIn" },
  { Icon: Send,      href: "https://t.me/samarawiz",          label: "Telegram" },
];

/* ---------- Menu structure (data-driven) ---------- */
const MENU = {
  products: {
    label: "Products",
    tagline: "Authentic Samara SKUs — GST-billed",
    accent: "Shop by Category",
    align: "left",
    testid: "nav-products",
    href: "/products?category=health",
    items: [
      { to: "/products?category=health",        testid: "nav-products-health",   icon: Leaf,          label: "Health & Wellness", sub: "Juices, capsules & immunity" },
      { to: "/products?category=education",     testid: "nav-products-education", icon: GraduationCap, label: "Education",        sub: "IT & digital-skill courses" },
      { to: "/products?category=agriculture",   testid: "nav-products-agriculture", icon: Wheat,      label: "Agriculture",       sub: "Farming — Coming Soon" },
      { to: "/products?category=fashion",       testid: "nav-products-fashion",  icon: Shirt,         label: "Fashion & Apparel", sub: "Saree, Suit-length, Salwar Suit", accent: true },
      { to: "/products?category=personal_care", testid: "nav-products-personal", icon: Droplet,       label: "Personal Care",     sub: "Skincare, hair & everyday care" },
      { to: "/products?category=combo",         testid: "nav-products-combo",    icon: Gift,          label: "Combos",            sub: "Curated starter bundles" },
    ],
  },
  opportunity: {
    label: "Smart Opportunity",
    tagline: "Learn. Share. Serve. Grow.",
    accent: "About Opportunity",
    align: "left",
    testid: "nav-opportunity",
    href: "/opportunity",
    items: [
      { to: "/opportunity",     testid: "nav-opp-overview",  icon: Target,     label: "Opportunity Overview", sub: "The Samara Wiz platform" },
      { to: "/get-associated",  testid: "nav-opp-associated",icon: UserPlus,   label: "How to Get Associated", sub: "Six simple steps to begin" },
      { to: "/plan",            testid: "nav-opp-plan",      icon: TrendingUp, label: "Business Plan",         sub: "Associate login required", accent: true },
      { to: "/rewards",         testid: "nav-opp-rewards",   icon: Trophy,     label: "Rewards and Recognition", sub: "Ranks, awards & lifestyle rewards" },
    ],
  },
  gallery: {
    label: "Events / Gallery",
    tagline: "Events, trips and recognition",
    accent: "Photo Gallery",
    align: "left",
    testid: "nav-gallery",
    href: "/gallery",
    items: [
      { to: "/gallery#events",       testid: "nav-gallery-events",       icon: CalendarDays, label: "Events",              sub: "Training summits & meetups" },
      { to: "/gallery#trips",        testid: "nav-gallery-trips",        icon: Plane,        label: "Trips",               sub: "Trip Fund achievers" },
      { to: "/gallery#achievements", testid: "nav-gallery-achievements", icon: Medal,        label: "Achievements",        sub: "Rank progressions" },
    ],
  },
};

/* ---------- Reusable dropdown component ---------- */
function NavDropdown({ config }) {
  const align = config.align === "right" ? "right-0" : "left-0";
  return (
    <div className="relative group" data-testid={`${config.testid}-wrapper`}>
      <Link
        to={config.href}
        data-testid={config.testid}
        className="relative inline-flex items-center gap-1 text-royal/85 hover:text-royal transition-colors py-1"
      >
        {config.label}
        <ChevronDown
          size={13}
          className="opacity-60 transition-transform duration-300 group-hover:rotate-180 group-hover:opacity-100"
        />
        {/* animated gold underline */}
        <span className="absolute -bottom-0.5 left-0 right-0 h-[2px] bg-gradient-to-r from-gold via-gold-deep to-gold scale-x-0 group-hover:scale-x-100 origin-center transition-transform duration-300" />
      </Link>

      {/* Dropdown panel */}
      <div
        className={`absolute ${align} top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 min-w-[340px] z-50`}
      >
        <div className="relative bg-white border border-line/70 rounded-sm shadow-[0_20px_50px_-15px_rgba(11,61,145,0.25)] overflow-hidden">
          {/* Gold accent notch */}
          <div className={`absolute top-0 ${align === "right-0" ? "right-6" : "left-6"} -translate-y-1/2 w-3 h-3 bg-white border-t border-l border-line/70 rotate-45`} />

          {/* Header strip */}
          <div className="relative px-5 py-3.5 bg-gradient-to-r from-royal via-royal-deep to-royal text-white border-b border-gold/30">
            <div className="absolute inset-0 opacity-[0.10] mix-blend-overlay [background-image:repeating-linear-gradient(45deg,#fff_0_1px,transparent_1px_8px)]" />
            <div className="relative flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[9px] tracking-[0.3em] uppercase text-gold/90">
                  {config.accent}
                </div>
                <div className="font-serif text-base text-white/95 leading-tight mt-0.5 truncate">
                  {config.label}
                </div>
              </div>
              <div className="text-[10px] italic text-white/60 max-w-[160px] text-right leading-tight">
                {config.tagline}
              </div>
            </div>
          </div>

          {/* Menu items */}
          <ul className="py-2">
            {config.items.map((it, idx) =>
              it.divider ? (
                <li key={`div-${idx}`} className="my-1 mx-4 border-t border-dashed border-line" />
              ) : (
                <li key={it.to}>
                  <Link
                    to={it.to}
                    data-testid={it.testid}
                    className={`relative flex items-start gap-3 px-5 py-2.5 group/item transition-colors ${
                      it.danger
                        ? "text-rose hover:bg-rose/5"
                        : it.accent
                        ? "text-gold-deep hover:bg-gold/5"
                        : "text-royal/80 hover:bg-ivory"
                    }`}
                  >
                    {/* Left gold accent bar (animates on hover) */}
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-0 group-hover/item:h-8 bg-gold transition-all duration-300" />
                    <div
                      className={`w-9 h-9 grid place-items-center border rounded-sm shrink-0 transition-all ${
                        it.danger
                          ? "bg-rose/10 border-rose/20 text-rose"
                          : it.accent
                          ? "bg-gold/10 border-gold/30 text-gold-deep"
                          : "bg-royal/[0.06] border-royal/15 text-royal group-hover/item:bg-royal group-hover/item:text-gold group-hover/item:border-royal"
                      }`}
                    >
                      <it.icon size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div
                        className={`text-sm font-medium leading-tight ${
                          it.danger ? "text-rose" : it.accent ? "text-gold-deep" : "text-royal"
                        }`}
                      >
                        {it.label}
                      </div>
                      {it.sub && (
                        <div className="text-[11px] text-navy/55 mt-0.5 leading-snug">
                          {it.sub}
                        </div>
                      )}
                    </div>
                    <ChevronRight
                      size={14}
                      className="mt-2.5 text-navy/25 group-hover/item:text-gold group-hover/item:translate-x-1 transition-all shrink-0"
                    />
                  </Link>
                </li>
              )
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ---------- Simple flat nav link (no dropdown) with underline animation ---------- */
function NavLink({ to, testid, children, icon: Icon }) {
  const { pathname } = useLocation();
  const active = pathname === to;
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`relative inline-flex items-center gap-1.5 py-1 transition-colors ${
        active ? "text-royal font-medium" : "text-royal/85 hover:text-royal"
      }`}
    >
      {Icon && <Icon size={14} className="opacity-70" />}
      {children}
      <span
        className={`absolute -bottom-0.5 left-0 right-0 h-[2px] bg-gradient-to-r from-gold via-gold-deep to-gold origin-center transition-transform duration-300 ${
          active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100 hover:scale-x-100"
        }`}
      />
    </Link>
  );
}

/* ---------- Mobile accordion ---------- */
function MobileGroup({ config, closeAll }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-line rounded-sm overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm text-royal font-medium hover:bg-ivory transition-colors"
      >
        <span className="inline-flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-gold rounded-full" />
          {config.label}
        </span>
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180 text-gold" : "text-royal/60"}`} />
      </button>
      {open && (
        <ul className="border-t border-line bg-ivory/40 py-1">
          {config.items.filter((i) => !i.divider).map((it) => (
            <li key={it.to}>
              <Link
                to={it.to}
                onClick={closeAll}
                className={`flex items-center gap-3 px-4 py-2.5 text-sm ${
                  it.danger ? "text-rose" : it.accent ? "text-gold-deep" : "text-royal/80"
                } hover:bg-white`}
              >
                <it.icon size={14} className="opacity-70 shrink-0" />
                <span className="flex-1">{it.label}</span>
                <ChevronRight size={12} className="text-navy/30" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const dashHome = user
    ? user.role === "superadmin" ? "/superadmin"
    : user.role === "admin" ? "/admin"
    : user.role === "franchise" ? "/franchise"
    : "/dashboard"
    : "/login";

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-lg border-b border-line/80 shadow-[0_1px_0_rgba(11,61,145,0.04)]">
      {/* Slim social/utility strip */}
      <div className="hidden md:flex items-center justify-between max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-8 text-[11px] text-navy/60 border-b border-line/60">
        <div className="flex items-center gap-1.5">
          <span className="text-gold">✦</span>
          <span className="uppercase tracking-[0.25em]">Health · Education · Beauty</span>
        </div>
        <div className="flex items-center gap-3">
          {SOCIAL_LINKS.map(({ Icon, href, label }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`nav-social-${label.toLowerCase()}`}
              aria-label={label}
              className="text-navy/50 hover:text-gold transition-colors"
            >
              <Icon size={13} />
            </a>
          ))}
          <span className="w-px h-3.5 bg-line" />
          <a href="mailto:support@samarawiz.com" className="hover:text-gold">
            support@samarawiz.com
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-6 text-sm">
          <NavLink to="/" testid="nav-home" icon={HomeIcon}>Home</NavLink>
          <NavLink to="/about" testid="nav-about" icon={Info}>About</NavLink>
          <NavDropdown config={MENU.products} />
          <NavDropdown config={MENU.opportunity} />
          <NavLink to="/offers" testid="nav-offers">Offers</NavLink>
          <NavDropdown config={MENU.gallery} />
          <NavLink to="/contact" testid="nav-contact" icon={Phone}>Contact</NavLink>
        </nav>

        <div className="flex items-center gap-3">
          {user && (
            <Link
              to="/cart"
              data-testid="nav-cart"
              className="relative p-2 text-royal hover:text-gold transition-colors"
            >
              <ShoppingCart size={20} />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-gold text-royal text-[10px] font-bold rounded-full w-5 h-5 grid place-items-center shadow-md">
                  {count}
                </span>
              )}
            </Link>
          )}
          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              <Link
                to={dashHome}
                data-testid="nav-dashboard"
                className="px-4 py-2 text-sm bg-gradient-to-r from-royal to-royal-deep text-white hover:from-royal-deep hover:to-royal transition-all rounded-sm inline-flex items-center gap-2 shadow-sm"
              >
                <LayoutDashboard size={16} /> {user.user_code}
              </Link>
              <button
                data-testid="nav-logout"
                onClick={() => {
                  logout();
                  nav("/");
                }}
                className="p-2 text-royal/70 hover:text-rose transition-colors"
              >
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <div className="hidden sm:flex items-center gap-2">
              <Link
                to="/login"
                data-testid="nav-login"
                className="px-4 py-2 text-sm text-royal hover:text-gold transition-colors"
              >
                Login
              </Link>
              <Link
                to="/register"
                data-testid="nav-register"
                className="px-5 py-2 text-sm bg-gradient-to-r from-gold to-gold-deep text-royal hover:from-royal hover:to-royal-deep hover:text-gold transition-all rounded-sm font-medium inline-flex items-center gap-2 shadow-sm"
              >
                <User size={14} /> Register
              </Link>
            </div>
          )}
          {user && (
            <Link
              to="/register"
              data-testid="nav-register-logged-in"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-widest text-gold-deep hover:text-royal border border-gold/40 hover:border-royal rounded-sm transition-colors"
              title="Register a new member / IBA"
            >
              <User size={12} /> Register
            </Link>
          )}
          <button
            data-testid="nav-menu-toggle"
            className="lg:hidden p-2 text-royal"
            onClick={() => setOpen(!open)}
          >
            <Menu size={20} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden border-t border-line bg-white max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-2 text-sm">
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-3 border border-line rounded-sm text-royal font-medium hover:bg-ivory"
            >
              <HomeIcon size={14} /> Home
            </Link>
            <MobileGroup config={MENU.products} closeAll={() => setOpen(false)} />
            <Link to="/about" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-3 border border-line rounded-sm text-royal font-medium hover:bg-ivory"><Info size={14} /> About</Link>
            <MobileGroup config={MENU.opportunity} closeAll={() => setOpen(false)} />
            <Link to="/offers" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-3 border border-line rounded-sm text-royal font-medium hover:bg-ivory"><Tag size={14} /> Offers</Link>
            <MobileGroup config={MENU.gallery} closeAll={() => setOpen(false)} />
            <Link to="/contact" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-3 border border-line rounded-sm text-royal font-medium hover:bg-ivory"><Phone size={14} /> Contact</Link>

            <div className="pt-3 mt-2 border-t border-line flex flex-col gap-2">
              {!user && (
                <>
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    className="px-4 py-3 border border-line rounded-sm text-royal text-center"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setOpen(false)}
                    className="px-4 py-3 bg-gradient-to-r from-gold to-gold-deep text-royal font-semibold rounded-sm text-center inline-flex items-center justify-center gap-2"
                  >
                    <User size={14} /> Register
                  </Link>
                </>
              )}
              {user && (
                <>
                  <Link
                    to={dashHome}
                    onClick={() => setOpen(false)}
                    className="px-4 py-3 bg-royal text-white rounded-sm text-center inline-flex items-center justify-center gap-2"
                  >
                    <LayoutDashboard size={14} /> Dashboard ({user.user_code})
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setOpen(false);
                      nav("/");
                    }}
                    className="px-4 py-3 border border-rose text-rose rounded-sm inline-flex items-center justify-center gap-2"
                  >
                    <LogOut size={14} /> Logout
                  </button>
                </>
              )}
            </div>

            <div className="flex gap-3 pt-3 border-t border-line mt-2 justify-center">
              {SOCIAL_LINKS.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="p-2 border border-line rounded-sm text-navy/60 hover:text-gold hover:border-gold/40"
                >
                  <Icon size={14} />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
      <AnnouncementBar />
      <ProductTicker />
    </header>
  );
}
